// If we add more roles in the future, we can add them here.
// This type basically prevents passing in an empty array, which would

import { exhaustiveSwitchError } from './exhaustiveSwitchError'

// always be a permission error unless the user is an admin.
export type PermissibleRoles =
  | [UserRole]
  | [UserRole, UserRole]
  | [UserRole, UserRole, UserRole]
  | [UserRole, UserRole, UserRole, UserRole]
  | [UserRole, UserRole, UserRole, UserRole, UserRole]
  | [UserRole, UserRole, UserRole, UserRole, UserRole, UserRole]

type PermissibleRolesOrAll = PermissibleRoles | 'all'

// Typescript will fail if the db enum UserRole changes and this isn't updated
export type UserRole =
  | 'admin'
  | 'manager'
  | 'priceAndRenewalsUpdater'
  | 'priceUpdater'
  | 'specialsAndCompsUpdater'
  | 'specialsUpdater'
  | 'viewer'
// This is a hack to enforce that ALL_ROLES is always up to date with UserRole
const roleToTrue: Record<UserRole, true> = {
  admin: true,
  manager: true,
  priceAndRenewalsUpdater: true,
  priceUpdater: true,
  specialsAndCompsUpdater: true,
  specialsUpdater: true,
  viewer: true,
}
const ALL_ROLES = Object.keys(roleToTrue) as PermissibleRoles

export const userRoleToDescription: Record<UserRole, string> = {
  admin:
    'Complete system access. Can manage all settings, users, properties, pricing, and renewals.',
  manager:
    'Property-level control. Can manage property settings, pricing, and renewals. Cannot modify company settings or users.',
  priceAndRenewalsUpdater:
    'Limited editor access. Can update new lease pricing and manage renewals only.',
  priceUpdater: 'Basic editor access. Can update new lease pricing only.',
  specialsAndCompsUpdater:
    'Can create and manage specials and comps. Cannot change settings, update pricing, or manage renewals.',
  specialsUpdater:
    'Can create and manage specials. Cannot change settings, update pricing, or manage renewals.',
  viewer:
    'Read-only access. Can create pricing suggestions and comments on renewals. Cannot change settings, update pricing, or manage renewals.',
}

type SimpleAction = 'read' | 'write'
type SimplePermissions = Record<SimpleAction, PermissibleRolesOrAll>
export enum CrudAction {
  Create = 'create',
  Read = 'read',
  Update = 'update',
  Delete = 'delete',
}
type CrudPermissions = Record<CrudAction, PermissibleRolesOrAll>
type ModelRolePermissions = SimplePermissions | CrudPermissions

// Add new models and permissions here
export enum ModelForPermission {
  Self = 'self',
  User = 'user',
  Firm = 'firm',
  Property = 'property',
  ProspectiveProperty = 'prospectiveProperty',
  Portfolio = 'portfolio',
  Pricing = 'pricing',
  CompProperty = 'compProperty',
  Job = 'job',
  Notification = 'notification',
  Special = 'special',
  UnitType = 'unitType',
  Unit = 'unit',
  Amenity = 'amenity',
  Report = 'report',
  RenewalOffer = 'renewalOffer',
  RenewalOfferSuggestion = 'renewalOfferSuggestion',
  RenewalRequest = 'renewalRequest',
  RenewalRequestComment = 'renewalRequestComment',
  TimeseriesGroup = 'timeseriesGroup',
  PricingReviewSuggestion = 'pricingReviewSuggestion',
  MarketRentAdjustment = 'marketRentAdjustment',
  AutoPilot = 'autoPilot',
  PriceChangeLimitOverride = 'priceChangeLimitOverride',
  RecipientGroup = 'recipientGroup',
  Guestcard = 'guestcard',
  ChatConversation = 'chatConversation',
}
const modelToPermissions: Record<ModelForPermission, ModelRolePermissions> = {
  user: {
    read: 'all',
    write: ['admin'],
  },
  self: {
    read: 'all',
    write: 'all',
  },
  firm: {
    read: 'all',
    write: ['admin'],
  },
  property: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  prospectiveProperty: {
    read: ['admin'],
    write: ['admin'],
  },
  autoPilot: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  portfolio: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  pricing: {
    read: 'all',
    write: ['admin', 'manager', 'priceAndRenewalsUpdater', 'priceUpdater'],
  },
  compProperty: {
    read: 'all',
    write: [
      'admin',
      'manager',
      'priceAndRenewalsUpdater',
      'priceUpdater',
      'specialsAndCompsUpdater',
    ],
  },
  job: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  notification: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  special: {
    read: 'all',
    write: [
      'admin',
      'manager',
      'priceAndRenewalsUpdater',
      'priceUpdater',
      'specialsAndCompsUpdater',
      'specialsUpdater',
    ],
  },
  unitType: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  unit: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  amenity: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  report: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  renewalOffer: {
    read: 'all',
    write: ['admin', 'manager', 'priceAndRenewalsUpdater'],
  },
  renewalOfferSuggestion: {
    read: 'all',
    write: 'all',
  },
  renewalRequest: {
    read: 'all',
    write: ['admin', 'manager', 'priceAndRenewalsUpdater'],
  },
  renewalRequestComment: {
    read: 'all',
    write: 'all',
  },
  timeseriesGroup: {
    read: 'all',
    write: 'all',
  },
  pricingReviewSuggestion: {
    read: 'all',
    write: 'all',
  },
  marketRentAdjustment: {
    read: 'all',
    write: ['admin', 'manager', 'priceAndRenewalsUpdater', 'priceUpdater'],
  },
  priceChangeLimitOverride: {
    read: 'all',
    write: ['admin', 'manager'],
  },
  recipientGroup: {
    read: 'all',
    write: ['admin'],
  },
  guestcard: {
    read: 'all',
    write: ['admin'],
  },
  // This really refers to reading ones own conversations,
  // not all for the firm
  chatConversation: {
    read: 'all',
    write: 'all',
  },
}

const modelToCrudPermissions = Object.entries(modelToPermissions).reduce(
  (acc, [model, permissions]) => {
    if ('create' in permissions) {
      acc[model as ModelForPermission] = permissions as CrudPermissions
    } else {
      acc[model as ModelForPermission] = {
        create: permissions.write,
        read: permissions.read,
        update: permissions.write,
        delete: permissions.write,
      }
    }
    return acc
  },
  {} as Record<ModelForPermission, CrudPermissions>
)

export function getPermissibleRolesForModelAction(
  model: ModelForPermission,
  actionOrActions: CrudAction | CrudAction[]
): PermissibleRoles {
  const crudPermissions = modelToCrudPermissions[model]
  const actions = Array.isArray(actionOrActions) ? actionOrActions : [actionOrActions]
  const permissibleRoles = actions.map(action => crudPermissions[action])
  // find the intersection of all permissible roles
  const intersection = permissibleRoles.reduce((acc, roles) => {
    if (acc === 'all') {
      return roles
    }
    if (roles === 'all') {
      return acc
    }
    return acc.filter(role => roles.includes(role)) as PermissibleRoles
  }, 'all' as PermissibleRolesOrAll)
  return intersection === 'all' ? ALL_ROLES : intersection
}

export function doesUserHavePermissionForModelAction<
  User extends { role: UserRole; admin?: boolean },
>(user: User, model: ModelForPermission, action: CrudAction): boolean {
  // Rentana Admins can take all actions
  return (
    (getPermissibleRolesForModelAction(model, action).includes(user.role) || user?.admin) ?? false
  )
}

type ModelActionKey = `${ModelForPermission}-read` | `${ModelForPermission}-write`
const modelActionDescriptions: Record<ModelActionKey, string> = {
  'self-read': 'View your own profile',
  'self-write': 'Edit your own profile',
  'user-read': 'View user information',
  'user-write': 'Manage users',
  'firm-read': 'View company settings',
  'firm-write': 'Edit company settings',
  'property-read': 'View properties',
  'property-write': 'Edit properties',
  'prospectiveProperty-read': 'View prospective properties',
  'prospectiveProperty-write': 'Edit prospective properties',
  'portfolio-read': 'View portfolios',
  'portfolio-write': 'Manage portfolios',
  'pricing-read': 'View pricing',
  'pricing-write': 'Update pricing',
  'compProperty-read': 'View comp properties',
  'compProperty-write': 'Manage comp properties',
  'job-read': 'View background jobs',
  'job-write': 'Manage background jobs',
  'notification-read': 'View notifications',
  'notification-write': 'Manage notifications',
  'special-read': 'View specials',
  'special-write': 'Manage specials',
  'unitType-read': 'View unit types',
  'unitType-write': 'Manage unit types',
  'unit-read': 'View units',
  'unit-write': 'Manage units',
  'amenity-read': 'View amenities',
  'amenity-write': 'Manage amenities',
  'report-read': 'View reports',
  'report-write': 'Manage reports',
  'renewalOffer-read': 'View renewal offers',
  'renewalOffer-write': 'Manage renewal offers',
  'renewalOfferSuggestion-read': 'View renewal offer suggestions',
  'renewalOfferSuggestion-write': 'Create renewal offer suggestions',
  'renewalRequest-read': 'View renewal requests',
  'renewalRequest-write': 'Manage renewal requests',
  'renewalRequestComment-read': 'View renewal request comments',
  'renewalRequestComment-write': 'Create renewal request comments',
  'timeseriesGroup-read': 'View timeseries groups',
  'timeseriesGroup-write': 'Manage timeseries groups',
  'pricingReviewSuggestion-read': 'View pricing review suggestions',
  'pricingReviewSuggestion-write': 'Create pricing review suggestions',
  'marketRentAdjustment-read': 'View market rent adjustments',
  'marketRentAdjustment-write': 'Create market rent adjustments',
  'autoPilot-read': 'View auto-pilot settings',
  'autoPilot-write': 'Manage auto-pilot settings',
  'priceChangeLimitOverride-read': 'View price change limit overrides',
  'priceChangeLimitOverride-write': 'Manage price change limit overrides',
  'recipientGroup-read': 'View recipient groups',
  'recipientGroup-write': 'Manage recipient groups',
  'guestcard-read': 'View leads',
  'guestcard-write': 'Manage leads',
  'chatConversation-read': 'View chat conversations',
  'chatConversation-write': 'Manage chat conversations',
}

export interface PermissionDetail {
  permission: string
  description: string
  granted: boolean
}

export function listModelAccessForUserRole(role: UserRole): PermissionDetail[] {
  const permissions: PermissionDetail[] = []

  Object.entries(modelToPermissions).forEach(([model, modelPermissions]) => {
    const actions: SimpleAction[] =
      'create' in modelPermissions ? ['read', 'write'] : ['read', 'write']

    actions.forEach(action => {
      const permissionKey = `${model}.${action}` as ModelActionKey
      const description = modelActionDescriptions[permissionKey]

      let granted: boolean
      if (action === 'read') {
        granted = getPermissibleRolesForModelAction(
          model as ModelForPermission,
          CrudAction.Read
        ).includes(role)
      } else {
        // For write, check if user can update as a proxy for all write permissions. Currently, all
        // models use the same roles for create, update, and delete operations (see
        // modelToCrudPermissions where write permissions map to all three actions). However, this
        // assumption may not always hold if we introduce more granular permissions in the future,
        // so Update is used here as a representative action.
        granted = getPermissibleRolesForModelAction(
          model as ModelForPermission,
          CrudAction.Update
        ).includes(role)
      }

      permissions.push({
        permission: permissionKey,
        description,
        granted,
      })
    })
  })

  return permissions
}

export function labelForUserRole(role: UserRole): string {
  switch (role) {
    case 'admin':
      return 'Admin'
    case 'manager':
      return 'Manager'
    case 'priceAndRenewalsUpdater':
      return 'Price & Renewals Editor'
    case 'priceUpdater':
      return 'Price Editor'
    case 'specialsAndCompsUpdater':
      return 'Specials & Comps Editor'
    case 'specialsUpdater':
      return 'Specials Editor'
    case 'viewer':
      return 'Viewer'
    default:
      throw exhaustiveSwitchError(role)
  }
}
